import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { getTopicBySlug, getTopicName, getTopicDesc, LEVELS } from '../data/topicCatalog';
import { getLessonsForTopic, isTopicUnlocked, UNLOCK_PERCENT } from '../data/topicLessons';
import { languageLabel } from '../data/languageNames';
import './TopicDetail.css';

const lockedText = {
    en: { title: 'This level is still locked', desc: 'Finish {percent}% of the {prev} lessons to open {level}.', back: 'Back' },
    it: { title: 'Questo livello è ancora bloccato', desc: 'Completa il {percent}% delle lezioni {prev} per aprire il livello {level}.', back: 'Indietro' },
    es: { title: 'Este nivel aún está bloqueado', desc: 'Completa el {percent}% de las lecciones {prev} para abrir el nivel {level}.', back: 'Atrás' },
    fr: { title: 'Ce niveau est encore verrouillé', desc: 'Termine {percent} % des leçons {prev} pour ouvrir le niveau {level}.', back: 'Retour' },
    pt: { title: 'Este nível ainda está bloqueado', desc: 'Conclua {percent}% das lições {prev} para abrir o nível {level}.', back: 'Voltar' },
};

const levelColors = {
    A1: { bg: '#E1F5EE', border: '#9FE1CB', text: '#085041', badge: '#9FE1CB' },
    A2: { bg: '#E6F1FB', border: '#B5D4F4', text: '#0C447C', badge: '#B5D4F4' },
    B1: { bg: '#EEEDFE', border: '#CECBF6', text: '#3C3489', badge: '#CECBF6' },
    B2: { bg: '#FAEEDA', border: '#FAC775', text: '#633806', badge: '#FAC775' },
    C1: { bg: '#FAECE7', border: '#F5C4B3', text: '#993C1D', badge: '#F5C4B3' },
    C2: { bg: '#FAEEDA', border: '#FAC775', text: '#633806', badge: '#FAC775' },
};

function TopicDetail() {
    const navigate = useNavigate();
    const { topicSlug } = useParams();
    const { interfaceLanguage, t } = useLanguage();
    const { activeLanguage, activeLevel } = useUserLanguage();
    const [activeTab, setActiveTab] = useState('lessons');
    const [completedByTopic, setCompletedByTopic] = useState({});
    const [progressLoaded, setProgressLoaded] = useState(false);

    const slugToName = {
        'greetings-introductions': 'Greetings & introductions',
        'numbers-counting': 'Numbers & counting',
        'family-relationships': 'Family & relationships',
        'food-drink-basics': 'Food & drink basics',
        'home-living': 'Home & living',
        'colours-descriptions': 'Colours & descriptions',
        'shopping-market': 'Shopping & market',
        'getting-around': 'Getting around',
        'daily-routine': 'Daily routine',
        'weather-seasons': 'Weather & seasons',
        'health-body': 'Health & body',
        'celebrations-events': 'Celebrations & events',
        'at-the-cafe': 'At the cafe',
        'travel-transport': 'Travel & transport',
        'food-restaurants': 'Food & restaurants',
        'culture-history': 'Culture & history',
        'work-career': 'Work & career',
        'technology-media': 'Technology & media',
        'news-current-events': 'News & current events',
        'environment-society': 'Environment & society',
        'arts-entertainment': 'Arts & entertainment',
        'law-justice': 'Law & justice',
    };

    const normalizedSlug = topicSlug.replace(/%C3%A9/g, 'e').replace(/é/g, 'e');

    useEffect(() => {
        const loadProgress = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const lang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
            // All topics, because opening a level depends on how much of the level before it is finished
            const { data } = await supabase
                .from('user_progress')
                .select('topic_slug, lesson_slug')
                .eq('user_id', user.id)
                .eq('target_language', lang);
            const byTopic = {};
            (data || []).forEach(row => {
                if (!byTopic[row.topic_slug]) byTopic[row.topic_slug] = [];
                byTopic[row.topic_slug].push(row.lesson_slug);
            });
            setCompletedByTopic(byTopic);
            setProgressLoaded(true);
        };
        loadProgress();
    }, [activeLanguage]);

    const completedSlugs = completedByTopic[normalizedSlug] || [];

    const targetLangKey = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const targetLangLabel = targetLangKey.charAt(0).toUpperCase() + targetLangKey.slice(1);
    const isItalianTarget = targetLangKey === 'italian';
    const sharedTopic = getTopicBySlug(normalizedSlug);

    const topicName = (sharedTopic ? getTopicName(sharedTopic, interfaceLanguage) : null) || slugToName[normalizedSlug] || normalizedSlug
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    const baseTopic = {
        name: topicName,
        emoji: '📚',
        level: localStorage.getItem('vozoly_level') || 'A1',
        desc: `Learn ${topicName.toLowerCase()} in ${targetLangLabel} — vocabulary, grammar and real conversations with Gabi.`,
        lessons: getLessonsForTopic(normalizedSlug),
    };
    // Name, emoji, level and description come from the shared topic list.
    // The saved lesson descriptions contain Italian example words, so they are only shown to Italian learners.
    // Lesson completion comes from the user's saved progress, never from the demo data.
    const topic = {
        ...baseTopic,
        name: sharedTopic ? getTopicName(sharedTopic, interfaceLanguage) : baseTopic.name,
        emoji: sharedTopic ? sharedTopic.emoji : baseTopic.emoji,
        level: sharedTopic ? sharedTopic.level : baseTopic.level,
        desc: sharedTopic ? getTopicDesc(sharedTopic, interfaceLanguage) : baseTopic.desc,
        lessons: baseTopic.lessons.map(l => ({ ...l, completed: false, desc: isItalianTarget ? l.desc : '' })),
    };
    const lc = levelColors[topic.level] || levelColors.B1;

    // Text on this page comes from translationsMore.js (topicDetail section)
    const td = (key, values) => {
        let text = t(`topicDetail.${key}`);
        Object.keys(values || {}).forEach(name => { text = text.split(`{${name}}`).join(values[name]); });
        return text;
    };
    const targetLangWord = (() => {
        const label = languageLabel(targetLangKey, interfaceLanguage);
        return interfaceLanguage === 'en' ? label : label.toLowerCase();
    })();

    const userLevel = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
    const topicLocked = progressLoaded && !isTopicUnlocked(normalizedSlug, userLevel, completedByTopic);
    const lk = (key) => {
        const dict = lockedText[interfaceLanguage] || lockedText.en;
        const prevLevel = LEVELS[Math.max(0, LEVELS.indexOf(topic.level) - 1)];
        return (dict[key] || lockedText.en[key])
            .replace('{percent}', String(UNLOCK_PERCENT))
            .replace('{prev}', prevLevel)
            .replace('{level}', topic.level);
    };

    const completedCount = topic.lessons.filter(l => {
        const slug = l.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        return l.completed || completedSlugs.includes(slug);
    }).length;
    const totalCount = topic.lessons.length;
    const progressPct = Math.round((completedCount / totalCount) * 100);

    const nextLesson = topic.lessons.find(l => {
        const slug = l.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        return !l.completed && !completedSlugs.includes(slug);
    });

    if (topicLocked) {
        return (
            <div className="td-screen">
                <div className="td-top-bar" style={{ background: lc.bg, borderBottom: `0.5px solid ${lc.border}` }}>
                    <div className="td-top-row">
                        <button className="td-back" onClick={() => navigate(-1)} style={{ color: lc.text }}>←</button>
                        <div className="td-level-badge" style={{ background: lc.badge, color: lc.text }}>{topic.level}</div>
                    </div>
                    <div className="td-hero">
                        <div className="td-emoji">🔒</div>
                        <div className="td-name" style={{ color: lc.text }}>{lk('title')}</div>
                        <div className="td-desc">{lk('desc')}</div>
                    </div>
                </div>
                <div style={{ padding: 24, textAlign: 'center' }}>
                    <button className="td-start-btn" onClick={() => navigate(-1)}>{lk('back')}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="td-screen">

            <div className="td-top-bar" style={{ background: lc.bg, borderBottom: `0.5px solid ${lc.border}` }}>
                <div className="td-top-row">
                    <button className="td-back" onClick={() => navigate(-1)} style={{ color: lc.text }}>←</button>
                    <div className="td-level-badge" style={{ background: lc.badge, color: lc.text }}>
                        {topic.level}
                    </div>
                </div>

                <div className="td-hero">
                    <div className="td-emoji">{topic.emoji}</div>
                    <div className="td-name" style={{ color: lc.text }}>{topic.name}</div>
                    <div className="td-desc">{topic.desc}</div>
                </div>

                <div className="td-progress-section">
                    <div className="td-progress-row">
                        <div className="td-progress-label" style={{ color: lc.text }}>
                            {td('lessonsComplete', { done: completedCount, total: totalCount })}
                        </div>
                        <div className="td-progress-pct" style={{ color: lc.text }}>{progressPct}%</div>
                    </div>
                    <div className="td-progress-track">
                        <div
                            className="td-progress-fill"
                            style={{ width: `${progressPct}%`, background: lc.border }}
                        ></div>
                    </div>
                </div>
            </div>

            {nextLesson && (
                <div className="td-next-lesson">
                    <div className="td-next-label">{td('upNext')}</div>
                    <div className="td-next-card">
                        <div className="td-next-info">
                            <div className="td-next-title">{nextLesson.title}</div>
                            <div className="td-next-desc">{nextLesson.desc}</div>
                            <div className="td-next-duration">⏱ {nextLesson.duration}</div>
                        </div>
                        <button className="td-start-btn" onClick={() => navigate(`/lesson/${normalizedSlug}__${nextLesson.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`)}>
                            {td('start')} →
                        </button>
                    </div>
                </div>
            )}

            <div className="td-tabs">
                <div
                    className={`td-tab ${activeTab === 'lessons' ? 'active' : ''}`}
                    onClick={() => setActiveTab('lessons')}
                >
                    {td('lessonsTab')}
                </div>
                <div
                    className={`td-tab ${activeTab === 'deeper' ? 'active' : ''}`}
                    onClick={() => setActiveTab('deeper')}
                >
                    {td('deeperTab')}
                </div>
                <div
                    className={`td-tab ${activeTab === 'about' ? 'active' : ''}`}
                    onClick={() => setActiveTab('about')}
                >
                    {td('aboutTab')}
                </div>
            </div>

            <div className="td-content">

                {activeTab === 'lessons' && (
                    <div className="td-lessons">
                        {topic.lessons.map((lesson, index) => (
                            <div
                                key={lesson.id}
                                className={`td-lesson-card ${lesson.completed || completedSlugs.includes(lesson.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')) ? 'completed' : ''}`}
                            >
                                <div className="td-lesson-number">
                                    {lesson.completed || completedSlugs.includes(lesson.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')) ? '✓' : index + 1}
                                </div>
                                <div className="td-lesson-info">
                                    <div className="td-lesson-title">{lesson.title}</div>
                                    <div className="td-lesson-desc">{lesson.desc}</div>
                                    <div className="td-lesson-duration">⏱ {lesson.duration}</div>
                                </div>
                                {(lesson.completed || completedSlugs.includes(lesson.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))) ? (
                                    <div className="td-lesson-completed-actions">
                                        <button
                                            className="td-lesson-results-btn"
                                            style={{ color: lc.text, borderColor: lc.border }}
                                            onClick={() => navigate(`/results/${normalizedSlug}/${lesson.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`)}
                                        >
                                            {td('results')}
                                        </button>
                                        <button
                                            className="td-lesson-btn"
                                            style={{ background: 'transparent', border: `1px solid ${lc.border}`, color: lc.text }}
                                            onClick={() => navigate(`/lesson/${normalizedSlug}__${lesson.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`)}
                                        >
                                            {td('redo')}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        className="td-lesson-btn"
                                        onClick={() => {
                                            const prevCompleted = index === 0 ||
                                                topic.lessons[index - 1]?.completed ||
                                                completedSlugs.includes(topic.lessons[index - 1]?.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
                                            if (prevCompleted) navigate(`/lesson/${normalizedSlug}__${lesson.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`);
                                        }}
                                    >
                                        {index === 0 || topic.lessons[index - 1]?.completed || completedSlugs.includes(topic.lessons[index - 1]?.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')) ? td('start') : '🔒'}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'deeper' && (
                    <div className="td-deeper">
                        {completedCount === 0 ? (
                            <div className="td-deeper-locked">
                                <div className="td-deeper-locked-icon">🔒</div>
                                <div className="td-deeper-locked-title">{td('completeFirst')}</div>
                                <div className="td-deeper-locked-desc">{td('completeFirstDesc')}</div>
                            </div>
                        ) : (
                            <>
                                <div className="td-deeper-intro">
                                    {td('deeperIntro')}
                                </div>
                                {[
                                    { slug: 'core-vocabulary', title: td('coreVocab'), desc: td('coreVocabDesc'), emoji: '📖', duration: `10 ${td('min')}` },
                                    { slug: 'grammar-focus', title: td('grammarFocus'), desc: td('grammarFocusDesc'), emoji: '📐', duration: `10 ${td('min')}` },
                                    { slug: 'conversation-practice', title: td('convPractice'), desc: td('convPracticeDesc', { lang: targetLangWord }), emoji: '💬', duration: `10 ${td('min')}` },
                                    { slug: 'luna-conversation', title: td('gabiConv'), desc: td('gabiConvDesc'), emoji: '🤖', duration: `15 ${td('min')}` },
                                ].map(item => (
                                    <div key={item.slug} className="td-deeper-card">
                                        <div className="td-deeper-emoji">{item.emoji}</div>
                                        <div className="td-deeper-info">
                                            <div className="td-deeper-title">{item.title}</div>
                                            <div className="td-deeper-desc">{item.desc}</div>
                                            <div className="td-deeper-duration">⏱ {item.duration}</div>
                                        </div>
                                        <button
                                            className="td-deeper-btn"
                                            style={{ background: lc.badge, color: lc.text }}
                                            onClick={() => item.slug === 'luna-conversation'
                                                ? navigate(`/luna/${normalizedSlug}`)
                                                : navigate(`/lesson/${normalizedSlug}__${item.slug}`)}
                                        >
                                            {td('start')} →
                                        </button>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'about' && (
                    <div className="td-about">
                        <div className="td-about-section">
                            <div className="td-about-label">{td('whatYoullLearn')}</div>
                            <div className="td-about-text">{topic.desc}</div>
                        </div>
                        <div className="td-about-section">
                            <div className="td-about-label">{td('levelLabel')}</div>
                            <div className="td-about-text">{topic.level} — {t(`topics.levelNames.${topic.level}`)}</div>
                        </div>
                        <div className="td-about-section">
                            <div className="td-about-label">{td('lessonsLabel')}</div>
                            <div className="td-about-text">{td('lessonsInfo', { n: totalCount, m: totalCount * 6 })}</div>
                        </div>
                        <div className="td-about-section">
                            <div className="td-about-label">{td('includes')}</div>
                            <div className="td-about-text">{td('includesText')}</div>
                        </div>
                    </div>
                )}

            </div>

            <div style={{ height: '24px' }}></div>

        </div>
    );
}

export default TopicDetail;